import type { AllTimeCommitsRelatedRes, RosterCompRes } from '@on3/api';
import { On3IconArrow } from '@on3/icons';
import { adjustColorForReadability } from '@on3/ui-lib/utils/accessibility/color';
import { getAbbreviatedClassRank } from '@on3/ui-lib/utils/getAbbreviatedClassRank';
import { teamLogoBorderColor } from '@on3/ui-lib/utils/retrieveColorStyle';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { On3Avatar } from 'components/Image/On3Avatar';
import { TextLink } from 'components/UI/Link/TextLink';
import {
  ConditionalLink,
  StarRating,
} from 'components/UI/StarRating/StarRating';
import { Text } from 'components/UI/Typography/Text';

import styles from './FormerAthletesItem.module.scss';

interface IFormerAthletesItem {
  athlete?: RosterCompRes;
  relatedModel?: AllTimeCommitsRelatedRes;
  index: number;
}

export const FormerAthletesItem = ({
  relatedModel,
  athlete,
  index,
}: IFormerAthletesItem) => {
  const {
    personDefaultMeasurements,
    person,
    position,
    personDefaultAsset,
    hsRecRanking,
    hsRec,
    draftInfo,
    collPso,
  } = athlete ?? {};
  const { classYear, fullName, slug } = person || {};
  const { formattedHeight, weight } = personDefaultMeasurements ?? {};
  const { abbreviation } = position ?? {};
  const { sport } = relatedModel ?? {};
  const { domain, source } = personDefaultAsset ?? {};
  const {
    industryOverallRank,
    industryPositionRank,
    industryStateRank,
    industryRating,
    industryStars,
  } = hsRecRanking ?? {};
  const { committedAsset } = hsRec ?? {};
  const formerAthleteHref =
    (slug && `/rivals/${slug}/industry-comparison/`) || '';
  const sluggedSport = slugify(sport?.name || 'football');
  const useLink =
    !!hsRec?.committedAsset?.slug && !!sluggedSport && !!classYear;
  const formerAthleteRanking = index + 1;
  const isPlayerDrafted = !!draftInfo;
  const linkColor = adjustColorForReadability(
    relatedModel?.organization?.primaryColor ?? '#fe3b1f',
  );

  const nflLogo = teamLogoBorderColor(
    draftInfo?.organization?.primaryColor ?? '#e9ecf3',
  );

  const collegeLogo = teamLogoBorderColor(
    collPso?.organization?.primaryColor ?? '#e9ecf3',
  );

  return (
    <div className={styles.block} role="listitem">
      <div className={styles.vitals}>
        <div className={styles.rankContainer}>
          <Text
            className={styles.overallRank}
            component="div"
            variant="subtitle1"
          >
            {<span> {formerAthleteRanking}</span>}
          </Text>
          <On3Avatar
            altText={`${fullName} Avatar` || ''}
            className={styles.avatar}
            domain={domain}
            source={source}
          />
        </div>

        <div>
          <div className={styles.name}>
            <TextLink
              className={styles.nameLink}
              href={`/rivals/${slug}/`}
              style={{ color: linkColor }}
            >
              {fullName}
            </TextLink>
          </div>
          <Text className={styles.playerVitals} component="div">
            <span className={styles.position}>{classYear ?? '-'}</span>
            <span className={styles.position}>{abbreviation ?? '-'}</span>
            <span className={styles.height}>{formattedHeight ?? '-'}</span>
            <span>{weight ?? '-'}</span>
          </Text>
          {(classYear ?? 0) >= 2005 && (
            <>
              <StarRating
                hasBorder={false}
                href={formerAthleteHref}
                isBoldRating={false}
                isConsensus
                rating={industryRating}
                stars={industryStars}
              />
              <div className={styles.rankingWrapper}>
                <Text className={styles.rankingItem} component="span">
                  <span>Natl</span>
                  <span className={styles.ranking}>
                    {industryOverallRank ?? '-'}
                  </span>
                </Text>
                <Text className={styles.rankingItem} component="span">
                  <span>Pos</span>
                  <span className={styles.ranking}>
                    {industryPositionRank ?? '-'}
                  </span>
                </Text>
                <Text className={styles.rankingItem} component="span">
                  <span>St</span>
                  <span className={styles.ranking}>
                    {industryStateRank ?? '-'}
                  </span>
                </Text>
              </div>
            </>
          )}
        </div>
      </div>
      <div className={styles.statusWrapper}>
        {classYear && (
          <Text
            className={styles.classWrapper}
            component="div"
            variant="subtitle1"
          >
            <span>Class:</span>
            <span className={styles.classYear}>{classYear}</span>
          </Text>
        )}
        {isPlayerDrafted && (
          <div className={styles.draft}>
            <ConditionalLink
              className={styles.draftLink}
              condition={useLink}
              href={`/college/${hsRec?.committedAsset?.slug}/${sluggedSport}/${classYear}/industry-comparison-commits/`}
              underline="none"
            >
              <On3Avatar
                className={styles.logo}
                domain={committedAsset?.defaultAsset?.domain}
                height={30}
                source={committedAsset?.defaultAsset?.source}
                teamAvatar
                title={hsRec?.committedAsset?.fullName}
                type={committedAsset?.defaultAsset?.type}
                width={30}
              />
              <On3IconArrow />
            </ConditionalLink>
            <div className={styles.statusLogoWrapper} style={nflLogo}>
              <On3Avatar
                className={styles.logo}
                domain={draftInfo?.organizationDefaultAsset?.domain}
                height={30}
                source={draftInfo?.organizationDefaultAsset?.source}
                teamAvatar
                title={draftInfo?.organization?.fullName}
                type={draftInfo?.organizationDefaultAsset?.type}
                width={30}
              />{' '}
            </div>

            <Text className={styles.picks}>
              <span>{draftInfo?.organization?.abbreviation}</span>
              <span>Pick {draftInfo?.overallPick}</span>
            </Text>
          </div>
        )}
        {!isPlayerDrafted && (
          <ConditionalLink
            className={styles.collegeLink}
            condition={useLink}
            href={`/college/${collPso?.organization?.slug}/${sluggedSport}/${classYear}/industry-comparison-commits/`}
            underline="none"
          >
            <div className={styles.statusLogoWrapper} style={collegeLogo}>
              <On3Avatar
                className={styles.logo}
                domain={collPso?.organizationAsset?.domain}
                height={30}
                source={collPso?.organizationAsset?.source}
                teamAvatar
                title={collPso?.organization?.fullName}
                type={collPso?.organizationAsset?.type}
                width={30}
              />
            </div>
            {
              <Text className={styles.picks}>
                {getAbbreviatedClassRank(collPso?.classYear ?? '')}
              </Text>
            }
          </ConditionalLink>
        )}
      </div>
    </div>
  );
};
